import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ContextMenu, ContextMenuList, Flex, Text, Textarea } from '@modusign/design-system';
import { FontType, TextAlign, TextField, TextFormat } from '@modusign/react-signing/types';
import i18n from 'i18next';
import { ChangeEvent, useCallback } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import { getSignerDisplayName } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getSignerDisplayName';
import { FormatContextMenuItem } from '@app/features/apply-react-signing/components/FieldSetupStep/FormatContextMenuItem';
import TextBaseFieldContextMenu from '@app/features/apply-react-signing/components/FieldSetupStep/TextBaseFieldContextMenu';
import { TextFieldContextMenuFormatRow } from '@app/features/apply-react-signing/components/FieldSetupStep/TextFieldContextMenuFormatRow';

type FormatType = TextFormat['type'];

const VALUE_DESCRIPTION_MEX_LENGTH = 40;

const CONTEXT_FORMAT_OPTIONS: Record<
  FormatType | 'TEXT',
  {
    title: string;
    example: string;
  }
> = {
  TEXT: {
    title: i18n.t('TextFieldContextMenuFormatRow-textTitle'),
    example: i18n.t('TextFieldContextMenuFormatRow-textExample'),
  },
  PHONE_NUMBER: {
    title: i18n.t('TextFieldContextMenuFormatRow-phoneNumberTitle'),
    example: '000-0000-0000',
  },
  BIZ_NUMBER: {
    title: i18n.t('TextFieldContextMenuFormatRow-bizNumberTitle'),
    example: '000-00-00000',
  },
  BANK_ACCOUNT_NUMBER: {
    title: i18n.t('TextFieldContextMenuFormatRow-bankAccountNumberTitle'),
    example: '0000-0000-0000-0000',
  },
} as const;

const formatTypes = Object.keys(CONTEXT_FORMAT_OPTIONS) as (keyof typeof CONTEXT_FORMAT_OPTIONS)[];

interface TextFieldContextMenuProps {
  dataLabelErrorMessage: string;
  field: TextField;
  participantOrder?: number;
  isDataLabelEditable?: boolean;
  isOpenFormatMenu: boolean;
  isValueDescriptionCollapsed: boolean;
  onDataLabelChange: (value: string) => void;
  onFontFamilyChange: (fontFamily: FontType) => void;
  onFontSizeChange: (fontSize: number) => void;
  onRequiredChange: (value: boolean) => void;
  onIsOpenFormatMenuChange: (value: boolean) => void;
  onTextAlignChange: (textAlign: TextAlign) => void;
  onFormatChange: (format: TextFormat | null) => void;
  onValueDescriptionChanged: (description: string) => void;
  onValueDescriptionCollapseMenuClick: () => void;
}

export function TextFieldContextMenu({
  field,
  dataLabelErrorMessage,
  isDataLabelEditable,
  participantOrder,
  isOpenFormatMenu,
  isValueDescriptionCollapsed,
  onIsOpenFormatMenuChange,
  onDataLabelChange,
  onFontSizeChange,
  onRequiredChange,
  onFontFamilyChange,
  onTextAlignChange,
  onFormatChange,
  onValueDescriptionChanged,
  onValueDescriptionCollapseMenuClick,
}: TextFieldContextMenuProps) {
  const handleDescriptionValueChanged = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.currentTarget;

      onValueDescriptionChanged(value.slice(0, VALUE_DESCRIPTION_MEX_LENGTH));
    },
    [onValueDescriptionChanged]
  );

  const valueDescription = field.config.valueDescription ?? '';

  if (field.participantReference.type !== 'SIGNER') {
    return null;
  }

  return (
    <TextBaseFieldContextMenu
      dataLabel={field.dataLabel}
      dataLabelErrorMessage={dataLabelErrorMessage}
      fontFamily={field.config.fontStyle.type}
      fontSize={field.config.fontStyle.size}
      isDataLabelEditable={isDataLabelEditable}
      isRequired={field.config.required}
      participantName={getSignerDisplayName(field.participantReference)}
      participantOrder={participantOrder}
      textAlign={field.config.textAlign}
      onDataLabelChange={onDataLabelChange}
      onFontFamilyChange={onFontFamilyChange}
      onFontSizeChange={onFontSizeChange}
      onRequiredToggle={(_, required) => onRequiredChange(required)}
      onTextAlignChange={onTextAlignChange}
    >
      <TextFieldContextMenuFormatRow
        contextMenu={
          <ContextMenu isOpen={isOpenFormatMenu}>
            <ContextMenuList left="70%" minWidth="184px" top={0}>
              <OutsideClickHandler onOutsideClick={() => onIsOpenFormatMenuChange(false)}>
                {formatTypes.map((formatType) => (
                  <FormatContextMenuItem
                    example={CONTEXT_FORMAT_OPTIONS[formatType].example}
                    key={formatType}
                    title={CONTEXT_FORMAT_OPTIONS[formatType].title}
                    onClick={() => {
                      onIsOpenFormatMenuChange(false);
                      onFormatChange(formatType === 'TEXT' ? null : { type: formatType });
                    }}
                  />
                ))}
              </OutsideClickHandler>
            </ContextMenuList>
          </ContextMenu>
        }
        label={CONTEXT_FORMAT_OPTIONS[field.config.format?.type ?? 'TEXT'].title}
        onInputClick={() => onIsOpenFormatMenuChange(!isOpenFormatMenu)}
      />
      {field.config.format === null && (
        <Box pb="12px">
          <ContextMenuCollapseMenu
            isCollapsed={isValueDescriptionCollapsed}
            label="입력 가이드"
            onClick={onValueDescriptionCollapseMenuClick}
          >
            <Textarea
              height="96px"
              maxLength={VALUE_DESCRIPTION_MEX_LENGTH}
              mt={2}
              sx={{
                resize: 'none',
                borderStyle: 'solid',
                borderWidth: '1px',
              }}
              value={valueDescription ?? ''}
              width="100%"
              onChange={handleDescriptionValueChanged}
            />
            <Box textAlign="right" width="100%">
              <Text color={valueDescription.length ? '#292929' : '#949494'}>
                {valueDescription.length}
              </Text>
              <Text color="#949494">/{VALUE_DESCRIPTION_MEX_LENGTH}자</Text>
            </Box>
          </ContextMenuCollapseMenu>
        </Box>
      )}
    </TextBaseFieldContextMenu>
  );
}

function ContextMenuCollapseMenu({
  isCollapsed,
  children,
  label,
  onClick,
}: {
  isCollapsed: boolean;
  label: string;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <>
      <Box textAlign="left" width="100%">
        <Flex
          alignContent="center"
          justifyContent="space-between"
          sx={{
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          width="100%"
          onClick={onClick}
        >
          <Flex flexDirection="column" height="32px" justifyContent="center" width="100%">
            <b>{label}</b>
          </Flex>

          <Flex height="32px" justifyContent="center" width="32px">
            <Flex flexDirection="column" justifyContent="center">
              <FontAwesomeIcon
                color="#757575"
                icon={isCollapsed ? faChevronDown : faChevronUp}
                size="sm"
              />
            </Flex>
          </Flex>
        </Flex>
      </Box>
      {!isCollapsed && children}
    </>
  );
}
